<template>
  <div v-if="checking" class="boot">
    <span class="boot-brand">loggob</span>
  </div>

  <LoginView v-else-if="!authed" @login-success="onLogin" />

  <AppShell
    v-else
    :username="username"
    :title="VIEW_TITLE[currentView]"
    :current-view="currentView"
    @navigate="onNavigate"
    @sign-out="onLogout"
  >
    <!-- LogsView stays mounted (v-show) while any other view is open so its
         scroll position, filter state, and manual live/paused toggle survive a
         round-trip. Its poll pauses via `settings-open` whenever Logs isn't the
         active view — the same mechanism the old settings modal used, which also
         sidesteps the change-password pwv re-login race. -->
    <LogsView
      v-show="currentView === 'logs'"
      :settings-open="currentView !== 'logs'"
      @unauthorized="onUnauthorized"
    />
    <SettingsView
      v-if="currentView === 'settings'"
      :username="username"
      :is-admin="isAdmin"
      @back="currentView = 'logs'"
      @sign-out="onSettingsSignOut"
    />
    <MetricsView v-else-if="currentView === 'metrics'" @unauthorized="onUnauthorized" />
    <TracesView v-else-if="currentView === 'traces'" @unauthorized="onUnauthorized" />
    <HomeView
      v-else-if="currentView === 'home'"
      @unauthorized="onUnauthorized"
      @go-dashboards="currentView = 'dashboards'"
    />
    <DashboardsView v-else-if="currentView === 'dashboards'" @unauthorized="onUnauthorized" />
    <AlertsView
      v-else-if="currentView === 'alerts'"
      :is-admin="isAdmin"
      @unauthorized="onUnauthorized"
    />
    <PlaceholderView
      v-else-if="placeholder"
      :key="currentView"
      :title="placeholder.title"
      :icon="placeholder.icon"
      :blurb="placeholder.blurb"
    />
  </AppShell>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { api, ApiError } from './api.js';
import { VIEW_TITLE, PLACEHOLDERS, type View } from './views.js';
import LoginView from './views/LoginView.vue';
import LogsView from './views/LogsView.vue';
import AppShell from './AppShell.vue';
import SettingsView from './views/SettingsView.vue';
import MetricsView from './views/MetricsView.vue';
import TracesView from './views/TracesView.vue';
import HomeView from './views/HomeView.vue';
import DashboardsView from './views/DashboardsView.vue';
import AlertsView from './views/AlertsView.vue';
import PlaceholderView from './views/PlaceholderView.vue';

const checking = ref(true);
const authed = ref(false);
const username = ref('');
const isAdmin = ref(false);
const currentView = ref<View>('logs');

const placeholder = computed(() => PLACEHOLDERS[currentView.value]);

/**
 * Where a fresh session lands (§3.4): Home when the user has starred a home
 * dashboard, Logs otherwise. Robust by construction — the setting read is
 * best-effort (a failure just means Logs) and a dangling id is pruned
 * server-side on read, so `home` is only preferred when it can render.
 */
async function landingView(): Promise<View> {
  // A shared logs link (URL query = filter state) always wins over the home
  // preference — the person followed that link to look at those logs.
  if (window.location.search) return 'logs';
  try {
    const homeId = await api.getHomeDashboardId();
    return homeId !== null ? 'home' : 'logs';
  } catch {
    return 'logs';
  }
}

async function checkSession() {
  try {
    const me = await api.me();
    username.value = me.username;
    isAdmin.value = me.isAdmin;
    authed.value = true;
    currentView.value = await landingView();
  } catch (e) {
    if (!(e instanceof ApiError)) {
      // Network error — treat as logged out; the login view can retry.
      console.error('Session check failed', e);
    }
    authed.value = false;
  } finally {
    checking.value = false;
  }
}

async function onLogin() {
  authed.value = true;
  // Pull the username + role for the shell/admin gating; non-fatal if it fails.
  try {
    const me = await api.me();
    username.value = me.username;
    isAdmin.value = me.isAdmin;
  } catch {
    /* leave username blank / non-admin */
  }
  currentView.value = await landingView();
}

async function onLogout() {
  try {
    await api.logout();
  } catch {
    /* ignore — clear the UI regardless */
  }
  authed.value = false;
  username.value = '';
  isAdmin.value = false;
  currentView.value = 'logs';
}

function onUnauthorized() {
  authed.value = false;
  username.value = '';
  isAdmin.value = false;
  currentView.value = 'logs';
}

function onNavigate(view: View) {
  currentView.value = view;
}

async function onSettingsSignOut() {
  await onLogout();
}

onMounted(checkSession);
</script>

<style scoped>
.boot {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
}
.boot-brand {
  font-family: var(--lg-font-mono);
  font-size: var(--lg-fs-xl);
  color: var(--lg-text-dim);
  animation: fade 1.2s ease-in-out infinite;
}
@keyframes fade {
  0%,
  100% {
    opacity: 0.4;
  }
  50% {
    opacity: 1;
  }
}
</style>
